import { useEffect, useState } from 'react'
import { fetchCatalogAdminParts, type CatalogAdminPartSummary } from '../api/client'
import { CatalogPartGridCard } from './CatalogPartGridCard'

const PART_TYPES = [
  '',
  'case',
  'dial',
  'hands',
  'movement',
  'strap',
  'bezel',
  'crystal',
  'crown',
]

const PROVIDERS = ['', 'lucius_atelier', 'namoki_mods']

const PAGE_SIZE = 50

export function CatalogAdmin() {
  const [text, setText] = useState('')
  const [partType, setPartType] = useState('')
  const [providerId, setProviderId] = useState('')
  const [availableOnly, setAvailableOnly] = useState(false)
  const [results, setResults] = useState<CatalogAdminPartSummary[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetchCatalogAdminParts({
          text: text.trim() || undefined,
          part_type: partType || undefined,
          provider_id: providerId || undefined,
          available_only: availableOnly,
          limit: PAGE_SIZE,
          offset: 0,
        })
        if (!cancelled) {
          setResults(response.results)
          setOffset(response.results.length)
          setHasMore(response.results.length === PAGE_SIZE)
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Search failed')
          setResults([])
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [text, partType, providerId, availableOnly])

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    setError(null)
    try {
      const response = await fetchCatalogAdminParts({
        text: text.trim() || undefined,
        part_type: partType || undefined,
        provider_id: providerId || undefined,
        available_only: availableOnly,
        limit: PAGE_SIZE,
        offset,
      })
      setResults((prev) => [...prev, ...response.results])
      setOffset((prev) => prev + response.results.length)
      setHasMore(response.results.length === PAGE_SIZE)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Load failed')
    } finally {
      setIsLoadingMore(false)
    }
  }

  return (
    <div className="debug-page catalog-admin-page">
      <header className="debug-header">
        <h1>Catalog</h1>
        <p className="debug-subtitle">
          Search parts and edit compatibility overrides that persist across re-ingestion.
        </p>
      </header>

      <div className="catalog-search-sticky">
        <form className="catalog-search-form" onSubmit={(event) => event.preventDefault()}>
          <div className="catalog-search-primary">
            <input
              type="search"
              className="catalog-search-input"
              placeholder="Search by name or description"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
            <button
              type="button"
              className="btn-secondary catalog-filters-toggle"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
            >
              Filters
            </button>
          </div>
          <div className={`catalog-search-filters${filtersOpen ? ' open' : ''}`}>
            <select value={partType} onChange={(event) => setPartType(event.target.value)}>
              {PART_TYPES.map((value) => (
                <option key={value || 'all'} value={value}>
                  {value ? value : 'All types'}
                </option>
              ))}
            </select>
            <select value={providerId} onChange={(event) => setProviderId(event.target.value)}>
              {PROVIDERS.map((value) => (
                <option key={value || 'all'} value={value}>
                  {value ? value : 'All providers'}
                </option>
              ))}
            </select>
            <label className="catalog-checkbox">
              <input
                type="checkbox"
                checked={availableOnly}
                onChange={(event) => setAvailableOnly(event.target.checked)}
              />
              Available only
            </label>
          </div>
        </form>
        <div className="catalog-results-meta">
          {isLoading ? (
            <span className="catalog-results-count">Searching…</span>
          ) : (
            <span className="catalog-results-count">
              {results.length} part{results.length !== 1 ? 's' : ''}
              {hasMore ? ' (more available)' : ''}
            </span>
          )}
        </div>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      {!isLoading && results.length === 0 ? (
        <p className="catalog-empty">No parts matched your search.</p>
      ) : (
        <div className="catalog-grid">
          {results.map((item) => (
            <CatalogPartGridCard
              key={item.part.id}
              part={item.part}
              hasOverride={item.has_override}
            />
          ))}
        </div>
      )}

      {hasMore && !isLoading ? (
        <div className="catalog-load-more">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void handleLoadMore()}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  )
}
